import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCIcon, RCCInput, RCCServicesApi, RCCTooltip } from "react-common-components";
import { DeepMap, FieldError, FieldValues, useForm } from "react-hook-form";
import Accordion from "src/components/Accordion";
import CheckPermission from "src/components/CheckPermission";
import InputNumber from "src/components/InputNumber";
import List from "src/components/List";
import Title from "src/components/Title";
import { useAuth } from "src/context/Auth/context";
import { PermissionEnum } from "src/context/Auth/interface";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import stringToNumber from "src/utils/stringToNumber";
import { IComparatives, IRefDiagnosticComparatives} from "../../../interface";
import { changeComparativesSchema } from "../../../schemas";
import Attachments from "./attachments";
import IDiagnosticAttachmentsRef from "./attachments/interface";

const DiagnostiComparatives: React.ForwardRefRenderFunction<IRefDiagnosticComparatives, IComparatives> = ({
	data,
	criteria,
	callbackUpdateComparative
}, ref) => {

	const { translate, currentLanguage } = useTranslation();
	const { addToast } = RCCHookToast();
	const { checkIfHasSpecificPermission } = useAuth();

	const modalAttachment = useRef<IDiagnosticAttachmentsRef>(null);
	
	const [expandedAccordion, setExpandedAccordion] = useState(false);
	const [buttonLoadingState, setButtonLoadingState] = useState(false);

	const { 
		handleSubmit, 
		register, 
		reset, 
		control, 
		clearErrors, 
		formState: { errors }, 
		setError 
	} = useForm(); 

	const permisionToCreateUpdate = checkIfHasSpecificPermission("/diagnostics", PermissionEnum.CREATE_UPDATE);
	const permisionToCreateUpdateDelete = checkIfHasSpecificPermission("/diagnostics", PermissionEnum.CREATE_UPDATE_DELETE);

	const submitComparatives = async (dataX: any) => {
		try{
			setButtonLoadingState(true);
			clearErrors();
			const dataForm = dataX;

			dataForm.mdtbb005 = dataForm.mdtbb005?.map((mdtbb005: any) => ({
				maxScore: criteria.mda002Pontuacaomax ?? 0,
				mdb005Id: Number(mdtbb005.mdb005Id) ? parseInt(mdtbb005.mdb005Id, 10) : null,
				mdb005Pontuacao: typeof mdtbb005.mdb005Pontuacao === "string" ? stringToNumber(mdtbb005.mdb005Pontuacao, currentLanguage) || 0 : mdtbb005.mdb005Pontuacao,
				mdb005Complemento: mdtbb005.mdb005Complemento
			}));



			await changeComparativesSchema(translate).validate({...dataForm}, {
				abortEarly: false
			});

			await RCCServicesApi.postRequest({
				method: "Diagnostics/UpdateComparatives",
				body: {
					mdtbb005: dataX.mdtbb005
				}
			});
	
			addToast({
				color: "green",
				description: translate("Comparativos alterados com sucesso"),
				title: translate("Sucesso")
			});
			setButtonLoadingState(false);
		}catch(err: any){
			setButtonLoadingState(false);
			if(getValidationErrors(err, setError))
				return;
			standardErrorMessages(err, translate("Erro ao alterar comparativos"), addToast);
		}
	};

	const colScore = (row: any, index: number | undefined) => (<>
		<input 
			type="text"
			value={row.mdtbb005?.mdb005Id}
			{...register(`mdtbb005.[${index}].mdb005Id`)} 
			style={{ display: "none" }} 
			readOnly
		/>
		<InputNumber 
			styleInline="margin-left: 4px;height: unset;"
			name={`mdtbb005.[${index}].mdb005Pontuacao`}
			label=""
			allowNegative={false}
			control={control}
			defaultValue={row.mdtbb005?.mdb005Pontuacao ?? ""}
			disabled={!permisionToCreateUpdate}
		/>		
	</>
	);

	const colComplement = (row: any, index: number | undefined) => (
		<RCCGrid
			container
			item
			justifyContent="space-between"
			wrap="nowrap"
			alignItems="center"
			spacing={1}
		>
			<RCCGrid
				container
				item
			>
				<RCCInput 
					styleInline="margin-left: 4px; height: unset;"
					name={`mdtbb005.[${index}].mdb005Complemento`}
					label=""
					maxLength={400}
					register={register}
					defaultValue={row.mdtbb005?.mdb005Complemento ?? ""}
					disabled={!permisionToCreateUpdate}
				/>		
			</RCCGrid>
			
			<RCCGrid
				container
				item
				style={{ maxWidth: "50px" }}
				justifyContent="center"
			>
				<RCCTooltip 
					text={translate("Anexos")}
					position="left"
					color={row.mdtbb005?.attachmentsNumber > 0 ? "blue" : "grey"}
				>
					<RCCButton
						color={row.mdtbb005?.attachmentsNumber > 0 ? "blue" : "grey"}
						icon={{
							name: "attachment"
						}}
						layout="isRounded"
						onClick={() => {
							if(!row.mdtbb005?.mdb005Id) return; 
							modalAttachment.current?.openModalComparativeAttachments(row.mdtbb005?.mdb005Id,row.mdb004Descricao, callbackUpdateComparative, !permisionToCreateUpdateDelete);
						}}
					/>
				</RCCTooltip>	
			</RCCGrid>
		
		</RCCGrid>	
	);

	const handleAccordion = (expanded:boolean) => {
		setExpandedAccordion(expanded);
		if(!expanded)
			return;
		reset();
	};

	useImperativeHandle(ref, () => ({
		reset
	}));


	const getErrors = (errorsx: DeepMap<FieldValues, FieldError>) => {
		const errorMaxScore = errorsx.find((error: any) => error?.mdb005Pontuacao);
		if(!errorMaxScore)
			return <></>;
		return (
			<>
				{errorMaxScore && translate("Pontuação deve ser menor ou igual a pontuação máxima")}: {criteria.mda002Pontuacaomax}
			</>
		);
	};


	return (
		<>
			<Accordion 
				expanded={expandedAccordion}
				onChange={(e, expanded) => {e.stopPropagation();  handleAccordion(expanded); }}
				header={<>
					<RCCGrid container item wrap="nowrap">
						{translate("Comparativos")}
					</RCCGrid>
				</>}
			>
				<>
					{!data || data.length === 0 ? 
						(
							<RCCGrid 
								container 
								styleInline="margin: 10px"
							>
								<RCCAlert 
									title={translate("Atenção")}
									description={translate("Nenhum comparativo encontrado")}
								/>
							</RCCGrid>
						) : 
						<>
							<RCCGrid
								container
								item
							>
								{errors.mdtbb005 && (
									<RCCGrid container item 
										alignItems="center"
									>	
										<RCCIcon 
											name="errorOutline" 
											size={19} 
											color="var(--red)"
										/>
										<Title 
											color="red" 
											size="sm"
										>
											{getErrors(errors.mdtbb005)}
										</Title>
									</RCCGrid>
								)}

								<List 
									data={data}
									showHeader
									columns={[{
										idColumn: "mdb004Descricao",
										nameColumn: translate("Descrição"),
										justify: "left",
										xs: 12,
										sm: 5,
										truncate: true
									},{
										idColumn: "mdb005Pontuacao",
										nameColumn: translate("Pontuação"),
										justify: "left",
										xs: 12,
										sm: 2,
										truncate: true,
										cellRenderer: colScore
									},{
										idColumn: "mdb005Complemento",
										nameColumn: translate("Complemento"),
										justify: "left",
										xs: 12,
										sm: 5,
										truncate: true,
										cellRenderer: colComplement
									}]}
									layout="table"
								/>		
								<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
									<RCCGrid
										item
										container
										justifyContent="flex-end"
									>
										<RCCButton
											icon={{name: "checkCircle"}}
											color="green"
											style={{ marginTop: "1rem" }}
											size="sm"
											layout="default"
											loading={buttonLoadingState}
											onClick={handleSubmit(submitComparatives)}
										>
											{translate("Confirmar alteração")}
										</RCCButton>
									</RCCGrid>									
								</CheckPermission>
							</RCCGrid>	
						</>
					}
				</>
			</Accordion>

			<Attachments 
				ref={modalAttachment}
			/>
		</>
	);
};

export default forwardRef(DiagnostiComparatives);
